import ConversationItemParts from 'owa-mail-store/lib/store/schema/ConversationItemParts';
import getLocalePropertyForItem from '../services/getLocalePropertyForItem';
import { action } from 'satcheljs/lib/legacy';
import { ClientItem, mailStore } from 'owa-mail-store';
import {
    lazyCheckCanTranslate,
    lazyInitSupportedLanguages,
    lazyGetLocaleOfSupportedLanguageMap,
    lazyGetOgmaSupportedLanguageTag,
    lazyGetSupportedLanguageMap,
    lazyGetUserLanguageName,
    lazyGetIsUserLocaleSupported,
} from 'owa-translation';

const MINIMUM_ITEM_TEXT_LENGTH = 20;

export default action('setItemLocale')(async function setItemLocale(item: ClientItem) {
    if (isItemTooShort(item)) {
        return;
    }

    const initSupportedLanguages = await lazyInitSupportedLanguages.import();
    const getLocaleOfSupportedLanguageMap = await lazyGetLocaleOfSupportedLanguageMap.import();
    const getOgmaSupportedLanguageTag = await lazyGetOgmaSupportedLanguageTag.import();
    const getSupportedLanguageMap = await lazyGetSupportedLanguageMap.import();
    const getUserLanguageName = await lazyGetUserLanguageName.import();
    const getIsUserLocaleSupported = await lazyGetIsUserLocaleSupported.import();
    const checkCanTranslate = await lazyCheckCanTranslate.import();

    let locale = item.TranslationData.itemLocale;
    if (locale == null) {
        locale = await getLocalePropertyForItem(item);
    }

    const ogmaLocale = getOgmaSupportedLanguageTag(locale);

    // Load supported languages and check if user locale has changed since last time.
    const localeChanged =
        (await initSupportedLanguages()) ||
        getUserLanguageName() != item.TranslationData.userLanguage;

    if (localeChanged) {
        const conversationItemParts: ConversationItemParts = mailStore.conversations.get(
            item.ConversationId.Id
        );

        item.TranslationData.userLanguage = getUserLanguageName();
        item.TranslationData.itemOgmaLocale = ogmaLocale;
        item.TranslationData.itemLocaleName = getSupportedLanguageMap().get(
            ogmaLocale
        ).LanguageName;

        // If user locale is not supported, return
        if (!getIsUserLocaleSupported()) {
            // but, update isTranslatable before leaving
            item.TranslationData.isTranslatable = false;
            item.TranslationData.isShowingTranslation = false;
            item.TranslationData.translationText = null;
            conversationItemParts.subjectTranslationData.isShowingTranslation = false;
            conversationItemParts.subjectTranslationData.translationText = null;
            return;
        }

        if (item.TranslationData.itemLocale == null) {
            item.TranslationData.itemLocale = locale;
        }

        const isTranslatable = checkCanTranslate(getLocaleOfSupportedLanguageMap(), locale);
        item.TranslationData.isTranslatable = isTranslatable;
        if (!isTranslatable || localeChanged) {
            item.TranslationData.isShowingTranslation = false;
            item.TranslationData.translationText = null;
            conversationItemParts.subjectTranslationData.isShowingTranslation = false;
            conversationItemParts.subjectTranslationData.translationText = null;
        }
    }
});

// We do not want to suggest user to translate very short items as in most
// cases detected language is not correct for these items. Untill the language
// detection is fixed, we are checking if the item's text length is large enough
// to trust detected language.
function isItemTooShort(item: ClientItem): Boolean {
    return item.Preview.length < MINIMUM_ITEM_TEXT_LENGTH;
}
